import React, { useCallback, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Dialog } from "@/components/toolkit/Dialog";
import { Clickable } from "@/components/toolkit/Clickable";
import { TextInput } from "@/components/toolkit/TextInput";
import { SelectInput } from "@/components/toolkit/SelectInput";
import type { ProjectStore, DeploymentEnvironmentStore } from "@/app_store/agent";
import { DockerComposeSettings } from "./DockerComposeSettings";
import { DToolSettings } from "./DToolSettings";
import "./s.configure_deploy_env_modal.css";

const ENGINE_OPTIONS = [
    { value: "docker_compose", title: "Docker Compose" },
    { value: "dtool", title: "DTool" },
];

function EngineSettings(props: {
    engine: string;
    settings: Record<string, any>;
    onChange: (s: Record<string, any>) => void;
    getMetadata: (settings: Record<string, any>) => Promise<any>;
}) {
    const { engine, settings, onChange, getMetadata } = props;
    if (engine === "docker_compose") return <DockerComposeSettings settings={settings} onChange={onChange} getMetadata={getMetadata} />;
    if (engine === "dtool") return <DToolSettings settings={settings} onChange={onChange} getMetadata={getMetadata} />;
    return null;
}

export const ConfigureDeploymentEnvModal = observer(function ConfigureDeploymentEnvModal(props: {
    project: ProjectStore;
    activeId: string | null;
    onActiveIdChange: (id: string | null) => void;
    onClose: () => void;
}) {
    const { project, activeId, onActiveIdChange, onClose } = props;
    const deployment = project.deployment;
    const envs = Object.values(deployment?.envs ?? {});
    const activeEnv: DeploymentEnvironmentStore | null =
        activeId ? (deployment?.envs[activeId] ?? null) : null;

    const [localName, setLocalName] = useState(activeEnv?.name ?? "");
    const [localEngine, setLocalEngine] = useState(activeEnv?.engine_type ?? "docker_compose");
    const [localVaultId, setLocalVaultId] = useState<string>(activeEnv?.vault_id ?? "");
    const [localSettings, setLocalSettings] = useState<Record<string, any>>(activeEnv?.engine_settings ?? {});

    useEffect(() => {
        setLocalName(activeEnv?.name ?? "");
        setLocalEngine(activeEnv?.engine_type ?? "docker_compose");
        setLocalVaultId(activeEnv?.vault_id ?? "");
        setLocalSettings(activeEnv?.engine_settings ?? {});
    }, [activeId, activeEnv?.id]);

    const getMetadata = useCallback((settings: Record<string, any>) => {
        return deployment?.get_engine_metadata(localEngine, settings) ?? Promise.resolve({});
    }, [deployment, localEngine]);

    const saveActive = useCallback(async () => {
        if (!activeEnv) return;
        await activeEnv.update_settings(localName, localEngine, localVaultId || null, localSettings);
    }, [activeEnv, localName, localEngine, localVaultId, localSettings]);

    const addEnv = useCallback(async () => {
        if (!deployment) return;
        const entry = await deployment.add_env("", "docker_compose", null, {});
        if (entry?.id) onActiveIdChange(entry.id);
    }, [deployment, onActiveIdChange]);

    const removeEnv = useCallback(async (id: string) => {
        if (!deployment) return;
        const remaining = envs.filter(e => e.id !== id);
        await deployment.remove_env(id);
        onActiveIdChange(remaining[0]?.id ?? null);
    }, [deployment, envs, onActiveIdChange]);

    const handleClose = useCallback(async () => {
        await saveActive();
        onClose();
    }, [saveActive, onClose]);

    const vaultOptions = [
        { value: "", title: "— None —" },
        ...Object.values(project.vaults).map(v => ({
            value: v.id,
            title: v.name.trim() || "Unnamed vault",
        })),
    ];

    return (
        <Dialog title="Configure Deployment Environments" onClose={handleClose}>
            <div className="configure_deploy_env_modal__body">
                {envs.length === 0 ? (
                    <div className="configure_deploy_env_modal__empty">
                        <Clickable onClick={addEnv}>
                            <button className="configure_deploy_env_modal__empty_btn">
                                + Add environment
                            </button>
                        </Clickable>
                    </div>
                ) : (
                    <>
                        <div className="configure_deploy_env_modal__tabs_bar">
                            {envs.map(env => (
                                <div
                                    key={env.id}
                                    className={"configure_deploy_env_modal__tab" + (env.id === activeId ? " active" : "")}
                                    onClick={() => { saveActive(); onActiveIdChange(env.id); }}
                                >
                                    <span className="configure_deploy_env_modal__tab_label">
                                        {env.name.trim() || "Unnamed"}
                                    </span>
                                    <span
                                        className="configure_deploy_env_modal__tab_close"
                                        onClick={e => { e.stopPropagation(); removeEnv(env.id); }}
                                    >×</span>
                                </div>
                            ))}
                            <button
                                className="configure_deploy_env_modal__new_tab_btn"
                                onClick={addEnv}
                                title="Add environment"
                            >+</button>
                        </div>

                        {activeEnv && (
                            <div className="configure_deploy_env_modal__form">
                                <div className="configure_deploy_env_modal__field">
                                    <label className="configure_deploy_env_modal__field_label">Name</label>
                                    <TextInput
                                        value={localName}
                                        placeholder="Unnamed"
                                        autoFocus
                                        onChange={v => setLocalName(v)}
                                    />
                                </div>
                                <div className="configure_deploy_env_modal__field">
                                    <label className="configure_deploy_env_modal__field_label">Engine</label>
                                    <SelectInput
                                        value={localEngine}
                                        options={ENGINE_OPTIONS}
                                        onChange={v => { setLocalEngine(v); setLocalSettings({}); }}
                                    />
                                </div>
                                <div className="configure_deploy_env_modal__field">
                                    <label className="configure_deploy_env_modal__field_label">Vault</label>
                                    <SelectInput
                                        value={localVaultId}
                                        options={vaultOptions}
                                        onChange={v => setLocalVaultId(v)}
                                    />
                                </div>
                                <div className="configure_deploy_env_modal__section_title">
                                    Engine settings
                                </div>
                                <EngineSettings
                                    engine={localEngine}
                                    settings={localSettings}
                                    onChange={setLocalSettings}
                                    getMetadata={getMetadata}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="configure_deploy_env_modal__actions">
                <Clickable onClick={onClose}>
                    <button className="configure_deploy_env_modal__btn configure_deploy_env_modal__btn--cancel">
                        Cancel
                    </button>
                </Clickable>
                <Clickable onClick={handleClose}>
                    <button className="configure_deploy_env_modal__btn configure_deploy_env_modal__btn--save">
                        Save
                    </button>
                </Clickable>
            </div>
        </Dialog>
    );
});
